/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import moment from 'moment';
import {getUserTimeZoneOffset} from '@ion/core/src/timezone/timezone';
import {toTimeZoneUtcOffset} from '@ion/core/src/utils/DateUtil';

export type DateType = moment.Moment;

export type DateFacetType = {
  key: string;
  dates: {
    gte: string;
    lte: string;
  };
  dateList: string[];
  count: number;
};

export type DateFacets = {
  today: DateFacetType;
  yesterday: DateFacetType;
  lastThreeDays: DateFacetType;
  lastWeek: DateFacetType;
  lastMonth: DateFacetType;
}

export type AggregationBucket = {
  key: string;
  count: number;
  label?: string;
};

export type Aggregation = {
  name: string;
  value: {
    buckets: AggregationBucket[];
  };
};

export const DATE_FACET_FORMAT = 'YYYY-MM-DD';

const dateFromat = 'YYYY-MM-DDTHH:mm:ss.000[Z]';

/**
 * Defines the categories that should be displayed for each permanent tab.
 */
const allJobsCategories = [
  'progressStatus-terms',
  'tasks.metadata.platformId-terms',
  'username-terms',
  'createdOn-dateHistogram-month',
  'createdOn-dateHistogram-week',
  'createdOn-dateHistogram-day'
];

export const aggregationsCategoryMap = {
  'my-jobs' : [
    'progressStatus-terms',
    'tasks.metadata.platformId-terms',
    'createdOn-dateHistogram-month',
    'createdOn-dateHistogram-week',
    'createdOn-dateHistogram-day'
  ],
  jobs            : allJobsCategories,
  'watching-jobs' : allJobsCategories
};

/**
 * Defines the categories that should be displayed for the individual job page.
 */
export const jobCategories = [
  'progressStatus-terms',
  'deviceTaskStatus-terms',
  'otaDiagnosticCode-terms',
  'otaStatCode-terms',
  'createdOn-dateHistogram-month',
  'createdOn-dateHistogram-week',
  'createdOn-dateHistogram-day'
];

/**
 * Filters aggregations based on provided facet categories and separates date-based aggregations.
 * 
 * @param aggregations - The array of aggregations.
 * @param facetCategories - The categories of facets to filter by.
 * @returns - An object containing filtered aggregations and date aggregations.
 */
export const getFilteredAggregations = (
  aggregations: Aggregation[] = [],
  facetCategories: string[] = []
) => {
  const [
    platformIdAggregations,
    filteredAggregations,
    dateAggregations
  ] = aggregations?.reduce<[Aggregation[], Aggregation[], Aggregation[]]>(
    ([platformId, filtered, dates], aggregation) => {
      if (facetCategories?.includes(aggregation?.name)) {
        if (aggregation?.name?.includes('dateHistogram')) {
          dates.push(aggregation);
        } else if (aggregation?.name?.includes('platformId')) {
          platformId.push(aggregation);
        } else {
          filtered.push(aggregation);
        }
      }
      return [platformId, filtered, dates];
    },
    [[], [], []]
  );

  return {
    platformIdAggregations,
    filteredAggregations,
    dateAggregations
  };
};

/**
 * Provides a utility function to check if an aggregation name contains a specific term.
 * 
 * @param aggregationName - The name of the aggregation.
 * @param term            - The term to check for.
 */
export const containsAggregationTerm = function(aggregationName: string, term: string): boolean {
  return aggregationName?.includes(term);
};

/**
 * Generates a date range for `gte` and `lte`.
 *
 * @param startOffset - Number of days back from today for the start of the range.
 * @param endOffset   - Number of days back from today for the end of the range.
 */
const createDateRange = (startOffset: number, endOffset: number) => {
  const userTimeZoneOffset = getUserTimeZoneOffset();
  const startDate = moment().subtract(startOffset, 'days').startOf('day').format(dateFromat);
  const endDate   = moment().subtract(endOffset, 'days').endOf('day').format(dateFromat);
  return {
    gte : startDate,
    lte : endDate
  };
};

/**
 * Creates an array of date-only strings from `startOffset` to `endOffset`.
 *
 * @param startOffset - Number of days back from today for the start of the array.
 * @param endOffset   - Number of days back from today for the end of the array.
 */
const createArrayOfDates = (startOffset: number, endOffset: number) => {
  const startDate = moment().subtract(startOffset, 'days').startOf('day');
  const endDate   = moment().subtract(endOffset, 'days').endOf('day');
  const dateArray: string[] = [];
  for (let m = moment(startDate); m.isSameOrBefore(endDate); m.add(1, 'days')) {
    dateArray.push(m.format(DATE_FACET_FORMAT));
  }
  return dateArray;
};

/**
 * Defines the date facets for the facets drawer.
 */
export const dateFacets: DateFacets = {
  today : {
    key      : 'today',
    dates    : createDateRange(0, 0),
    dateList : createArrayOfDates(0, 0),
    count    : 0
  },
  yesterday : {
    key      : 'yesterday',
    dates    : createDateRange(1, 1),
    dateList : createArrayOfDates(1, 1),
    count    : 0
  },
  lastThreeDays : {
    key      : 'lastThreeDays',
    dates    : createDateRange(2, 0),
    dateList : createArrayOfDates(2, 0),
    count    : 0
  },
  lastWeek : {
    key      : 'lastWeek',
    dates    : createDateRange(6, 0),
    dateList : createArrayOfDates(6, 0),
    count    : 0
  },
  lastMonth : {
    key      : 'lastMonth',
    dates    : createDateRange(29, 0),
    dateList : createArrayOfDates(29, 0),
    count    : 0
  }
};

/**
 * Updates the count for each date range based on the histogram data.
 *
 * @param facets        - The date facets to update with counts.
 * @param histogramData - The histogram data containing counts.
 * @returns             - Updated facets with counts.
 */
export const updateFacetCounts = (facets: DateFacets, histogramData: Aggregation[] = []) => {
  Object.keys(facets).forEach((key) => {
    facets[key].count = 0;
  });
  const dates = histogramData.find((histogram) => histogram.name === 'createdOn-dateHistogram-day');
  if (!dates?.value?.buckets) {
    return facets;
  }
  dates.value.buckets.forEach((bucket) => {
    const bucketDate  = moment.utc(bucket.key).format(DATE_FACET_FORMAT);
    const bucketCount = bucket.count;
    Object.keys(facets).forEach((facetKey) => {
      const facet = facets[facetKey];
      if (facet.dateList.includes(bucketDate)) {
        facet.count += bucketCount;
      }
    });
  });
  return facets;
};

/**
 * Determines if every date facet has a count of zero.
 */
export const allDateFacetsEmpty = (facets: DateFacets) => {
  return Object.values(facets).every(item => item.count === 0);
};
